import { distanceBetweenCoordinates } from '@core/logic';
import type { Vector } from '@core/types';
import type { Trench, Point } from '@trenches/types';

const MIN_ENDPOINT_SNAP_THRESHOLD = 0.3;

export const getEndpointSnapThreshold = (radius: number): number =>
  Math.max(radius * 1.5, MIN_ENDPOINT_SNAP_THRESHOLD);

export interface EndpointSnapCandidate {
  distance: number;
  pointId: string;
}

interface ResolveEndpointSnapTargetProps {
  clickPoint: Vector;
  excludedPointId?: string;
  pointEntities: Record<string, Point | undefined>;
  selectedPointId?: string;
  threshold: number;
  trenchesInScope: Trench[];
}

export const resolveEndpointSnapTarget = ({
  clickPoint,
  excludedPointId,
  pointEntities,
  selectedPointId,
  threshold,
  trenchesInScope,
}: ResolveEndpointSnapTargetProps): EndpointSnapCandidate | undefined => {
  let closestCandidate: EndpointSnapCandidate | undefined;

  for (let i = 0; i < trenchesInScope.length; i += 1) {
    const pointIds = trenchesInScope[i]?.pointIds ?? [];
    if (pointIds.length === 0) continue;

    const endpointIds = [pointIds[0]];
    const lastPointId = pointIds[pointIds.length - 1];
    if (pointIds.length > 1 && lastPointId !== pointIds[0]) {
      endpointIds.push(lastPointId);
    }

    for (let j = 0; j < endpointIds.length; j += 1) {
      const pointId = endpointIds[j];
      if (!pointId || pointId === selectedPointId || pointId === excludedPointId) continue;

      const point = pointEntities[pointId];
      if (!point) continue;

      const distance = distanceBetweenCoordinates(clickPoint, point);
      if (distance > threshold) continue;

      if (!closestCandidate || distance < closestCandidate.distance) {
        closestCandidate = { pointId, distance };
      }
    }
  }

  return closestCandidate;
};
